// ============================================
// Biomarker Alias Service
//
// Maps OCR-extracted biomarker names to canonical
// IDs from the biomarkerCatalog. Handles lab-specific
// naming (LabCorp, Quest, MyChart) and unit conversion.
// ============================================

import type { ExtractedBiomarker } from '../types/labUpload';
import {
  resolveAmbiguity,
  buildCandidate,
  type AmbiguityInput,
  type DocumentContext,
  type SourceLocation,
  type SectionHeader,
  type SiblingBiomarker,
} from './ambiguityProtocol';

// ── Alias Map ──
// Keys are lowercase; values are canonical biomarkerCatalog IDs.

export const ALIAS_MAP: Record<string, string> = {
  // CBC
  'wbc': 'wbc',
  'white blood cell': 'wbc',
  'white blood cell count': 'wbc',
  'white blood cells': 'wbc',
  'white blood cells (wbc)': 'wbc',
  'rbc': 'rbc',
  'red blood cell': 'rbc',
  'red blood cell count': 'rbc',
  'red blood cells': 'rbc',
  'red blood cells (rbc)': 'rbc',
  'hemoglobin': 'hemoglobin',
  'hgb': 'hemoglobin',
  'hb': 'hemoglobin',
  'hematocrit': 'hematocrit',
  'hct': 'hematocrit',
  'mcv': 'mcv',
  'mean corpuscular volume': 'mcv',
  'mean corpuscular volume (mcv)': 'mcv',
  'mch': 'mch',
  'mean corpuscular hemoglobin': 'mch',
  'mean corpuscular hemoglobin (mch)': 'mch',
  'mchc': 'mchc',
  'mean corpuscular hgb conc': 'mchc',
  'mean corpuscular hemoglobin concentration (mchc)': 'mchc',
  'rdw': 'rdw',
  'red cell distribution width': 'rdw',
  'red cell distribution width (rdw)': 'rdw',
  'rdw-cv': 'rdw',
  'platelets': 'platelets',
  'platelet count': 'platelets',
  'plt': 'platelets',
  'mpv': 'mpv',
  'mean platelet volume': 'mpv',
  'mean platelet volume (mpv)': 'mpv',
  'neutrophils': 'neutrophils',
  'neutrophils (absolute)': 'neutrophils',
  'absolute neutrophils': 'neutrophils',
  'neutrophil count': 'neutrophils',
  'neutrophils (%)': 'neutrophils_pct',
  'lymphocytes': 'lymphocytes',
  'lymphocytes (absolute)': 'lymphocytes',
  'absolute lymphocytes': 'lymphocytes',
  'lymphocyte count': 'lymphocytes',
  'lymphocytes (%)': 'lymphocytes_pct',
  'monocytes': 'monocytes',
  'monocytes (absolute)': 'monocytes',
  'absolute monocytes': 'monocytes',
  'monocyte count': 'monocytes',
  'monocytes (%)': 'monocytes_pct',
  'eosinophils': 'eosinophils',
  'eosinophils (absolute)': 'eosinophils',
  'absolute eosinophils': 'eosinophils',
  'eosinophil count': 'eosinophils',
  'eosinophils (%)': 'eosinophils_pct',
  'basophils': 'basophils',
  'basophils (absolute)': 'basophils',
  'absolute basophils': 'basophils',
  'basophil count': 'basophils',
  'basophils (%)': 'basophils_pct',

  // CMP
  'glucose': 'glucose',
  'glucose, serum': 'glucose',
  'fasting glucose': 'fasting_glucose',
  'glucose, fasting': 'fasting_glucose',
  'bun': 'bun',
  'blood urea nitrogen': 'bun',
  'blood urea nitrogen (bun)': 'bun',
  'urea nitrogen': 'bun',
  'creatinine': 'creatinine',
  'creatinine, serum': 'creatinine',
  'egfr': 'egfr',
  'gfr': 'egfr',
  'estimated gfr': 'egfr',
  'estimated glomerular filtration rate (egfr)': 'egfr',
  'estimated glomerular filtration rate': 'egfr',
  'egfr non-afr. american': 'egfr',
  'egfr if non-african am': 'egfr',
  'sodium': 'sodium',
  'sodium, serum': 'sodium',
  'na': 'sodium',
  'potassium': 'potassium',
  'potassium, serum': 'potassium',
  'k': 'potassium',
  'chloride': 'chloride',
  'chloride, serum': 'chloride',
  'cl': 'chloride',
  'co2': 'co2',
  'carbon dioxide': 'co2',
  'carbon dioxide (co2)': 'co2',
  'carbon dioxide, total': 'co2',
  'bicarbonate': 'co2',
  'calcium': 'calcium',
  'calcium, serum': 'calcium',
  'ca': 'calcium',
  'total protein': 'total_protein',
  'protein, total': 'total_protein',
  'protein, total, serum': 'total_protein',
  'albumin': 'albumin',
  'albumin, serum': 'albumin',
  'globulin': 'globulin',
  'globulin, total': 'globulin',
  'a/g ratio': 'ag_ratio',
  'albumin/globulin ratio': 'ag_ratio',
  'bun/creatinine ratio': 'bun_creatinine_ratio',
  'bun / creatinine ratio': 'bun_creatinine_ratio',
  'bun/creat ratio': 'bun_creatinine_ratio',
  'bilirubin': 'bilirubin',
  'bilirubin, total': 'bilirubin',
  'total bilirubin': 'bilirubin',
  'alp': 'alp',
  'alkaline phosphatase': 'alp',
  'alkaline phosphatase (alp)': 'alp',
  'alk phos': 'alp',
  'alkaline phosphatase, s': 'alp',
  'ast': 'ast',
  'sgot': 'ast',
  'ast (sgot)': 'ast',
  'aspartate aminotransferase': 'ast',
  'aspartate aminotransferase (ast)': 'ast',
  'alt': 'alt',
  'sgpt': 'alt',
  'alt (sgpt)': 'alt',
  'alanine aminotransferase': 'alt',
  'alanine aminotransferase (alt)': 'alt',
  'ggt': 'ggt',
  'gamma-glutamyl transferase': 'ggt',
  'gamma gt': 'ggt',

  // Lipid Panel
  'total cholesterol': 'total_cholesterol',
  'cholesterol, total': 'total_cholesterol',
  'cholesterol': 'total_cholesterol',
  'ldl': 'ldl',
  'ldl cholesterol': 'ldl',
  'ldl cholesterol calc': 'ldl',
  'ldl cholesterol (calculated)': 'ldl',
  'ldl calculation': 'ldl',
  'ldl calculated': 'ldl',
  'ldl-c': 'ldl',
  'ldl chol calc (nih)': 'ldl',
  'hdl': 'hdl',
  'hdl cholesterol': 'hdl',
  'hdl-c': 'hdl',
  'high-density lipoprotein cholesterol (hdl)': 'hdl',
  'high-density lipoprotein cholesterol': 'hdl',
  'high density lipoprotein cholesterol (hdl)': 'hdl',
  'triglycerides': 'triglycerides',
  'triglycerides, serum': 'triglycerides',
  'trigs': 'triglycerides',
  'vldl': 'vldl',
  'vldl cholesterol': 'vldl',
  'vldl cholesterol cal': 'vldl',
  'tc/hdl ratio': 'tc_hdl_ratio',
  'total cholesterol/hdl ratio': 'tc_hdl_ratio',
  'chol/hdl ratio': 'chol_hdl_ratio',
  'cholesterol/hdl ratio': 'chol_hdl_ratio',
  'non-hdl cholesterol': 'non_hdl_cholesterol',
  'non-high-density lipoprotein cholesterol (non-hdl)': 'non_hdl_cholesterol',
  'non-high-density lipoprotein cholesterol': 'non_hdl_cholesterol',
  'non hdl cholesterol': 'non_hdl_cholesterol',
  'non-hdl chol': 'non_hdl_cholesterol',

  // Advanced Lipid
  'apob': 'apolipoprotein_b',
  'apolipoprotein b': 'apolipoprotein_b',
  'apo b': 'apolipoprotein_b',
  'lp(a)': 'lpa',
  'lipoprotein(a)': 'lpa',
  'lipoprotein (a)': 'lpa',
  'ldl particle number': 'ldl_particle_number',
  'ldl-p': 'ldl_particle_number',
  'ldl particle size': 'ldl_small',
  'small ldl': 'ldl_small',
  'oxidized ldl': 'oxidized_ldl',
  'ox-ldl': 'oxidized_ldl',
  'omega-3 index': 'omega3_index',
  'omega 3 index': 'omega3_index',
  'omega-6/omega-3 ratio': 'omega6_omega3_ratio',

  // Thyroid
  'tsh': 'tsh',
  'thyroid stimulating hormone': 'tsh',
  'thyroid-stimulating hormone (tsh)': 'tsh',
  'thyroid-stimulating hormone': 'tsh',
  'thyrotropin': 'tsh',
  'tsh, 3rd generation': 'tsh',
  'free t3': 'free_t3',
  'triiodothyronine, free': 'free_t3',
  'ft3': 'free_t3',
  'free t3 (triiodothyronine)': 'free_t3',
  't3, free': 'free_t3',
  'free t4': 'free_t4',
  'thyroxine, free': 'free_t4',
  'ft4': 'free_t4',
  'free t4 (thyroxine)': 'free_t4',
  't4, free (direct)': 'free_t4',
  't4, free': 'free_t4',
  'reverse t3': 'reverse_t3',
  'rt3': 'reverse_t3',
  'reverse t3, serum': 'reverse_t3',
  'tpo antibodies': 'tpo_antibodies',
  'thyroid peroxidase ab': 'tpo_antibodies',
  'thyroid peroxidase (tpo) ab': 'tpo_antibodies',
  'anti-tpo': 'tpo_antibodies',
  'thyroglobulin antibodies': 'thyroglobulin_antibodies',
  'thyroglobulin ab': 'thyroglobulin_antibodies',
  'anti-thyroglobulin ab': 'thyroglobulin_antibodies',

  // Hormones — sex-dimorphic (resolved via SEX_DIMORPHIC_MAP below, defaults to female)
  'estradiol': 'estradiol_female',
  'estradiol, serum': 'estradiol_female',
  'e2': 'estradiol_female',
  'progesterone': 'progesterone_female',
  'progesterone, serum': 'progesterone_female',
  'fsh': 'fsh_female',
  'follicle stimulating hormone': 'fsh_female',
  'follicle-stimulating hormone (fsh)': 'fsh_female',
  'follicle-stimulating hormone': 'fsh_female',
  'fsh, serum': 'fsh_female',
  'lh': 'lh_female',
  'luteinizing hormone': 'lh_female',
  'lh, serum': 'lh_female',
  'dhea-s': 'dheas_female',
  'dhea-so4': 'dheas_female',
  'dhea sulfate': 'dheas_female',
  'dehydroepiandrosterone sulfate': 'dheas_female',
  'dhea-sulfate': 'dheas_female',
  'shbg': 'shbg_female',
  'sex hormone binding globulin': 'shbg_female',
  'sex horm binding glob': 'shbg_female',
  'testosterone, total': 'testosterone_total_male',
  'testosterone': 'testosterone_total_male',
  'testosterone, serum': 'testosterone_total_male',
  'total testosterone': 'testosterone_total_male',
  'free testosterone': 'free_testosterone_male',
  'testosterone, free': 'free_testosterone_male',
  'testosterone free': 'free_testosterone_male',
  'dht': 'dht',
  'dihydrotestosterone': 'dht',
  'prolactin': 'prolactin_male',
  'cortisol': 'cortisol_am',
  'cortisol, am': 'cortisol_am',
  'cortisol, total': 'cortisol_am',
  'cortisol am': 'cortisol_am',

  // Reproductive
  'amh': 'amh',
  'anti-mullerian hormone': 'amh',
  'anti-müllerian hormone': 'amh',

  // Metabolic
  'hba1c': 'hba1c',
  'hemoglobin a1c': 'hba1c',
  'a1c': 'hba1c',
  'glycohemoglobin': 'hba1c',
  'hgb a1c': 'hba1c',
  'fasting insulin': 'fasting_insulin',
  'insulin': 'fasting_insulin',
  'insulin, fasting': 'fasting_insulin',
  'homa-ir': 'homa_ir',
  'homa ir': 'homa_ir',
  'uric acid': 'uric_acid',
  'uric acid, serum': 'uric_acid',

  // Inflammatory
  'hs-crp': 'hscrp',
  'hscrp': 'hscrp',
  'high sensitivity crp': 'hscrp',
  'c-reactive protein': 'hscrp',
  'c-reactive protein, cardiac': 'hscrp',
  'crp, high sensitivity': 'hscrp',
  'homocysteine': 'homocysteine',
  'homocysteine, plasma': 'homocysteine',
  'esr': 'esr',
  'sed rate': 'esr',
  'sedimentation rate': 'esr',
  'erythrocyte sedimentation rate': 'esr',
  'fibrinogen': 'fibrinogen',
  'fibrinogen activity': 'fibrinogen',
  'il-6': 'il6',
  'interleukin-6': 'il6',
  'interleukin 6': 'il6',
  'tnf-alpha': 'tnf_alpha',
  'tnf alpha': 'tnf_alpha',
  'tumor necrosis factor alpha': 'tnf_alpha',

  // Nutritional
  'vitamin d': 'vitamin_d',
  'vitamin d, 25-hydroxy': 'vitamin_d',
  '25-hydroxy vitamin d': 'vitamin_d',
  'vitamin d 25-oh': 'vitamin_d',
  '25-oh vitamin d': 'vitamin_d',
  'vitamin d (25-oh)': 'vitamin_d',
  '25-hydroxyvitamin d': 'vitamin_d',
  'b12': 'b12',
  'vitamin b12': 'b12',
  'vitamin b-12': 'b12',
  'cobalamin': 'b12',
  'folate': 'folate',
  'folic acid': 'folate',
  'folate, serum': 'folate',
  'iron': 'iron',
  'iron, serum': 'iron',
  'iron, total': 'iron',
  'ferritin': 'ferritin',
  'ferritin, serum': 'ferritin',
  'magnesium': 'magnesium_rbc',
  'magnesium, rbc': 'magnesium_rbc',
  'magnesium rbc': 'magnesium_rbc',
  'rbc magnesium': 'magnesium_rbc',
  'zinc': 'zinc',
  'zinc, plasma': 'zinc',
  'zinc, serum': 'zinc',
  'tibc': 'tibc',
  'total iron binding capacity': 'tibc',
  'iron binding capacity': 'tibc',
  'iron saturation': 'iron_saturation',
  'iron sat': 'iron_saturation',
  '% saturation': 'iron_saturation',
  'transferrin saturation': 'iron_saturation',
  'selenium': 'selenium',
  'selenium, serum': 'selenium',
  'pth': 'pth',
  'parathyroid hormone': 'pth',
  'pth, intact': 'pth',

  // Autoimmune
  'ana': 'ana',
  'antinuclear antibodies': 'ana',
  'ana screen': 'ana',
  'rheumatoid factor': 'rheumatoid_factor',
  'rf': 'rheumatoid_factor',
  'anti-ccp': 'anti_ccp',
  'cyclic citrullinated peptide ab': 'anti_ccp',
  'ttg iga': 'ttg_iga',
  'tissue transglutaminase iga': 'ttg_iga',

  // Heavy Metals
  'mercury': 'mercury',
  'mercury, blood': 'mercury',
  'lead': 'lead',
  'lead, blood': 'lead',
  'arsenic': 'arsenic',
  'arsenic, blood': 'arsenic',
  'cadmium': 'cadmium',
  'cadmium, blood': 'cadmium',

  // Tumor Markers
  'psa': 'psa',
  'prostate specific antigen': 'psa',
  'psa, total': 'psa',
  'ca-125': 'ca125',
  'ca 125': 'ca125',
  'cancer antigen 125': 'ca125',
  'cea': 'cea',
  'carcinoembryonic antigen': 'cea',
  'afp': 'afp',
  'alpha-fetoprotein': 'afp',

  // Longevity
  'igf-1': 'igf1',
  'igf1': 'igf1',
  'insulin-like growth factor 1': 'igf1',
  'igf-1, lc/ms': 'igf1',
  'growth hormone': 'gh',
  'gh': 'gh',
  'hgh': 'gh',

  // BioReference lab abbreviations
  'polys': 'neutrophils', 'polys, abs. count': 'neutrophils_abs',
  'lymphs': 'lymphocytes', 'lymphs, abs. count': 'lymphocytes_abs',
  'monos': 'monocytes', 'monos, abs. count': 'monocytes_abs',
  'eos': 'eosinophils', 'eos, abs. count': 'eosinophils_abs',
  'basos': 'basophils', 'basos, abs. count': 'basophils_abs',
  'ig, abs. count': 'immature_granulocytes_abs',
  'e-gfr': 'egfr',
  'hdl chol., direct': 'hdl',
  'lipoprot. (ldl) direct': 'ldl',
  'vldl, calculated': 'vldl',
  'lipoprotein-a': 'lipoprotein_a',
  'apolipoprotein a1': 'apolipoprotein_a1',
  'apo. b/a1 ratio': 'apo_b_a1_ratio',
  'bilirubin, direct': 'bilirubin_direct',
  'hdl as % of cholesterol': 'hdl_percent',
  'testosterone, tot.,s.': 'testosterone_total_male',
  'sex horm.bind.glob.': 'shbg_female',
  'igf-1 (somatomedin-c)': 'igf1',
  'psa total': 'psa_total',
  'psa, free': 'psa_free',
  '% free psa': 'psa_free_percent',
  'oxldl': 'oxidized_ldl',
  'igf-1(somatomedin-c)': 'igf1',
  'esr(sed-rate)': 'esr', 'esr (sed-rate)': 'esr',
  'iron, % sat.': 'iron_saturation', 'iron, % sat': 'iron_saturation',
  'lead, blood(adult)': 'lead', 'lead, blood (adult)': 'lead',
  'mercury, whole blood': 'mercury',
  'selenium,ser/plasma': 'selenium', 'selenium, ser/plasma': 'selenium',
  'rheumatoid (rf) titer': 'rheumatoid_factor', 'rheumatoid(rf) titer': 'rheumatoid_factor',
};

// ── Sex-Dimorphic Routing ──
// When sex is known, these canonical IDs are swapped to the correct variant.
// Key: female default ID → { Male: male variant, Female: female variant }

const SEX_DIMORPHIC_MAP: Record<string, { Male: string; Female: string }> = {
  'estradiol_female': { Male: 'estradiol_male', Female: 'estradiol_female' },
  'progesterone_female': { Male: 'progesterone_male', Female: 'progesterone_female' },
  'fsh_female': { Male: 'fsh_male', Female: 'fsh_female' },
  'lh_female': { Male: 'lh_male', Female: 'lh_female' },
  'dheas_female': { Male: 'dheas_male', Female: 'dheas_female' },
  'shbg_female': { Male: 'shbg_male', Female: 'shbg_female' },
  'testosterone_total_male': { Male: 'testosterone_total_male', Female: 'testosterone_total_female' },
  'free_testosterone_male': { Male: 'free_testosterone_male', Female: 'free_testosterone_female' },
  'prolactin_male': { Male: 'prolactin_male', Female: 'prolactin_female' },
};

// ── Unit Conversion ──

interface UnitConversion {
  factor: number;
  targetUnit: string;
}

// Key format: "canonicalId:fromUnit"
const UNIT_CONVERSIONS: Record<string, UnitConversion> = {
  // Glucose: mmol/L → mg/dL
  'glucose:mmol/l': { factor: 18.018, targetUnit: 'mg/dL' },
  'fasting_glucose:mmol/l': { factor: 18.018, targetUnit: 'mg/dL' },

  // Cholesterol: mmol/L → mg/dL
  'total_cholesterol:mmol/l': { factor: 38.67, targetUnit: 'mg/dL' },
  'ldl:mmol/l': { factor: 38.67, targetUnit: 'mg/dL' },
  'hdl:mmol/l': { factor: 38.67, targetUnit: 'mg/dL' },
  'vldl:mmol/l': { factor: 38.67, targetUnit: 'mg/dL' },

  // Triglycerides: mmol/L → mg/dL
  'triglycerides:mmol/l': { factor: 88.57, targetUnit: 'mg/dL' },

  // Creatinine: µmol/L → mg/dL
  'creatinine:µmol/l': { factor: 0.0113, targetUnit: 'mg/dL' },
  'creatinine:umol/l': { factor: 0.0113, targetUnit: 'mg/dL' },

  // BUN: mmol/L → mg/dL
  'bun:mmol/l': { factor: 2.801, targetUnit: 'mg/dL' },

  // Calcium: mmol/L → mg/dL
  'calcium:mmol/l': { factor: 4.008, targetUnit: 'mg/dL' },

  // Iron: µmol/L → µg/dL
  'iron:µmol/l': { factor: 5.585, targetUnit: 'µg/dL' },
  'iron:umol/l': { factor: 5.585, targetUnit: 'µg/dL' },

  // Vitamin D: nmol/L → ng/mL
  'vitamin_d:nmol/l': { factor: 0.4006, targetUnit: 'ng/mL' },

  // B12: pmol/L → pg/mL
  'b12:pmol/l': { factor: 1.355, targetUnit: 'pg/mL' },

  // Folate: nmol/L → ng/mL
  'folate:nmol/l': { factor: 0.4413, targetUnit: 'ng/mL' },

  // Testosterone: nmol/L → ng/dL
  'testosterone_total_male:nmol/l': { factor: 28.842, targetUnit: 'ng/dL' },
  'testosterone_total_female:nmol/l': { factor: 28.842, targetUnit: 'ng/dL' },

  // Cortisol: nmol/L → µg/dL
  'cortisol_am:nmol/l': { factor: 0.03625, targetUnit: 'µg/dL' },

  // Homocysteine: no conversion needed (always µmol/L)

  // TSH: no conversion needed (always mIU/L or µIU/mL, equivalent)
};

// ── Exported Functions ──

/**
 * Resolve an OCR-extracted biomarker name to a canonical catalog ID.
 * When sex is provided, routes sex-dimorphic hormones to the correct variant.
 * Returns null if no match found.
 */
export function resolveAlias(rawName: string, sex?: 'Male' | 'Female'): string | null {
  const normalized = rawName.toLowerCase().trim();
  let baseId = ALIAS_MAP[normalized] ?? null;

  // Fallback: strip parenthesized abbreviation suffix — "Thyroid-Stimulating Hormone (TSH)" → "thyroid-stimulating hormone"
  // Also try the abbreviation itself — "(TSH)" → "tsh"
  if (!baseId) {
    const parenMatch = normalized.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
    if (parenMatch) {
      const withoutParen = parenMatch[1].trim();
      const abbreviation = parenMatch[2].trim();
      // C1/C2: Only try abbreviation if it's at least 3 chars (avoid "Ca", "Na", "K" collisions)
      baseId = ALIAS_MAP[withoutParen] ?? (abbreviation.length >= 3 ? ALIAS_MAP[abbreviation] : null) ?? null;
    }
  }

  if (!baseId) return null;

  // Route sex-dimorphic biomarkers to the correct variant
  if (sex && SEX_DIMORPHIC_MAP[baseId]) {
    return SEX_DIMORPHIC_MAP[baseId][sex];
  }

  return baseId;
}

/**
 * Convert a biomarker value from one unit to the platform's standard unit.
 * Returns the original value and unit if no conversion is needed.
 */
export function convertUnit(
  canonicalId: string,
  value: number,
  fromUnit: string,
): { value: number; unit: string } {
  const key = `${canonicalId}:${fromUnit.toLowerCase().trim()}`;
  const conversion = UNIT_CONVERSIONS[key];

  if (!conversion) {
    return { value, unit: fromUnit };
  }

  return {
    value: Math.round(value * conversion.factor * 100) / 100,
    unit: conversion.targetUnit,
  };
}

/**
 * Fuzzy match an OCR-extracted name against the alias map.
 * Uses Levenshtein distance with a configurable threshold.
 * Returns null if no match above the threshold is found.
 */
export function fuzzyMatch(
  rawName: string,
  threshold = 0.85,
): { id: string; confidence: number } | null {
  const normalized = rawName.toLowerCase().trim();

  // Try exact match first
  const exact = ALIAS_MAP[normalized];
  if (exact) return { id: exact, confidence: 1.0 };

  // Fuzzy search across all alias keys
  let bestMatch: { id: string; confidence: number } | null = null;

  for (const [alias, id] of Object.entries(ALIAS_MAP)) {
    const similarity = computeSimilarity(normalized, alias);
    if (similarity >= threshold && (!bestMatch || similarity > bestMatch.confidence)) {
      bestMatch = { id, confidence: similarity };
    }
  }

  return bestMatch;
}

/**
 * Resolve and optionally convert a single extracted biomarker.
 * When sex is provided, routes sex-dimorphic hormones to the correct variant.
 * Returns the biomarker with canonicalId and converted value/unit.
 */
export function resolveAndConvert(biomarker: ExtractedBiomarker, sex?: 'Male' | 'Female'): ExtractedBiomarker {
  // Resolve alias with sex-aware routing
  let canonicalId = resolveAlias(biomarker.rawName, sex);

  // Fallback to fuzzy match
  if (!canonicalId) {
    const fuzzy = fuzzyMatch(biomarker.rawName);
    if (fuzzy) {
      canonicalId = fuzzy.id;
      // Apply sex-dimorphic routing to fuzzy match result
      if (sex && SEX_DIMORPHIC_MAP[canonicalId]) {
        canonicalId = SEX_DIMORPHIC_MAP[canonicalId][sex];
      }
      biomarker.confidence = Math.min(biomarker.confidence, fuzzy.confidence);
      if (fuzzy.confidence < 1.0) {
        biomarker.parseWarnings.push(
          `Fuzzy matched "${biomarker.rawName}" to "${canonicalId}" (confidence: ${fuzzy.confidence.toFixed(2)})`,
        );
      }
    }
  }

  biomarker.canonicalId = canonicalId;

  // Convert units if we have a canonical ID and a value
  if (canonicalId && biomarker.value !== null) {
    const converted = convertUnit(canonicalId, biomarker.value, biomarker.unit);
    if (converted.unit !== biomarker.unit) {
      biomarker.parseWarnings.push(
        `Converted ${biomarker.value} ${biomarker.unit} → ${converted.value} ${converted.unit}`,
      );
      biomarker.value = converted.value;
      biomarker.unit = converted.unit;
    }
  }

  return biomarker;
}

// ── Ambiguity-Aware Resolution ──

/**
 * Known ambiguous short names that map to multiple candidates.
 * These fire the ambiguity protocol instead of guessing.
 */
const AMBIGUOUS_NAMES: Record<string, string[]> = {
  't': ['total_t3', 'testosterone_total_male'],
  't3': ['free_t3', 'total_t3'],
  't4': ['free_t4', 'total_t4'],
};

/**
 * Extended resolveAndConvert that routes ambiguous names through
 * the ambiguity protocol (CAP.02 D#5) before assignment.
 *
 * documentContext and sourceLocation are optional — when not provided
 * (e.g. from manual entry), ambiguous names default to unidentified.
 */
export function resolveAndConvertWithContext(
  biomarker: ExtractedBiomarker,
  sex?: 'Male' | 'Female',
  documentContext?: DocumentContext,
  sourceLocation?: SourceLocation,
  siblingBiomarkers?: SiblingBiomarker[],
  sectionHeaders?: SectionHeader[],
): ExtractedBiomarker {
  const normalized = biomarker.rawName.toLowerCase().trim();

  // Check if this is a known ambiguous name
  const ambiguousCandidateIds = AMBIGUOUS_NAMES[normalized];

  if (ambiguousCandidateIds) {
    // Build candidates with clinical groups
    const candidates = ambiguousCandidateIds
      .map(buildCandidate)
      .filter((c): c is NonNullable<typeof c> => c !== null);

    if (candidates.length >= 2) {
      const ctx: DocumentContext = documentContext ?? {
        sectionHeaders: sectionHeaders ?? [],
        siblingBiomarkers: siblingBiomarkers ?? [],
      };

      const loc: SourceLocation = sourceLocation ?? {
        page: biomarker.pageNumber,
        x: 0,
        y: 0,
      };

      const ambiguityInput: AmbiguityInput = {
        rawName: biomarker.rawName,
        candidates,
        sourceLocation: loc,
        extractedRange: biomarker.standardRange,
        extractedUnit: biomarker.unit,
        documentContext: ctx,
      };

      const result = resolveAmbiguity(ambiguityInput);

      if (result.resolved && result.canonicalId) {
        biomarker.canonicalId = result.canonicalId;
        biomarker.parseWarnings.push(result.resolutionNotes);

        // Apply sex-dimorphic routing to resolved ID
        if (sex && SEX_DIMORPHIC_MAP[biomarker.canonicalId]) {
          biomarker.canonicalId = SEX_DIMORPHIC_MAP[biomarker.canonicalId][sex];
        }

        // Convert units
        if (biomarker.canonicalId && biomarker.value !== null) {
          const converted = convertUnit(biomarker.canonicalId, biomarker.value, biomarker.unit);
          if (converted.unit !== biomarker.unit) {
            biomarker.parseWarnings.push(
              `Converted ${biomarker.value} ${biomarker.unit} → ${converted.value} ${converted.unit}`,
            );
            biomarker.value = converted.value;
            biomarker.unit = converted.unit;
          }
        }

        return biomarker;
      }

      // Unresolved — mark as unidentified, never guess
      biomarker.canonicalId = null;
      biomarker.parseWarnings.push(result.resolutionNotes);
      return biomarker;
    }
  }

  // Not ambiguous — use standard resolution
  return resolveAndConvert(biomarker, sex);
}

/**
 * Detect ambiguous fuzzy matches: when multiple alias entries
 * match at similar confidence levels, route through ambiguity protocol.
 */
export function fuzzyMatchWithAmbiguityCheck(
  rawName: string,
  threshold = 0.85,
): { id: string; confidence: number; ambiguous: boolean; candidateIds?: string[] } | null {
  const normalized = rawName.toLowerCase().trim();

  // Exact match — no ambiguity
  const exact = ALIAS_MAP[normalized];
  if (exact) return { id: exact, confidence: 1.0, ambiguous: false };

  // Collect all matches above threshold
  const matches: { id: string; alias: string; confidence: number }[] = [];

  for (const [alias, id] of Object.entries(ALIAS_MAP)) {
    const similarity = computeSimilarity(normalized, alias);
    if (similarity >= threshold) {
      matches.push({ id, alias, confidence: similarity });
    }
  }

  if (matches.length === 0) return null;

  // Sort by confidence descending
  matches.sort((a, b) => b.confidence - a.confidence);

  // Check if top matches resolve to different canonical IDs
  const topConfidence = matches[0].confidence;
  const closeMatches = matches.filter(
    (m) => topConfidence - m.confidence < 0.05,
  );

  const uniqueIds = new Set(closeMatches.map((m) => m.id));

  if (uniqueIds.size > 1) {
    // Multiple distinct canonical IDs at similar confidence — ambiguous
    return {
      id: matches[0].id,
      confidence: matches[0].confidence,
      ambiguous: true,
      candidateIds: [...uniqueIds],
    };
  }

  return { id: matches[0].id, confidence: matches[0].confidence, ambiguous: false };
}

// ── Internal Helpers ──

/**
 * Compute string similarity using Levenshtein distance, normalized to 0-1.
 */
function computeSimilarity(a: string, b: string): number {
  const maxLen = Math.max(a.length, b.length);
  if (maxLen === 0) return 1.0;
  return 1 - levenshteinDistance(a, b) / maxLen;
}

function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));

  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      dp[i][j] = a[i - 1] === b[j - 1]
        ? dp[i - 1][j - 1]
        : 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
    }
  }

  return dp[m][n];
}
